import React, { forwardRef, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Select from "react-select";
import { Button, Modal } from 'react-bootstrap';
import MaterialTable, { MTableToolbar } from 'material-table';
import {
  Delete, Edit, History, Lock, LockOpen,
} from '@material-ui/icons';

import './tableOverrides.less';
import api from '../../api';
import { localization, modifyDefaultOptions, sortByAlphaNum } from '../../helpers/table';
import { tableIcons, styledTable, styledTableHeading } from '../../helpers';

import BackBtn from '../../components/UI/BackBtn';
import DownloadLink from '../../components/common/DownloadLink';
import { dateRenderer } from '../../services/formatSvc';

import useToast from '../../hooks/useToast';
import ConfirmModal from '../../components/common/ConfirmModal';
import SPHistory from '../../components/admin/SPHistory';

const defaultModalState = {show: false, id: undefined}; 

export default function AdminStrats() {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [projects, setProjects] = useState([]);
  const [deleteProject, setDeleteProject] = useState(defaultModalState);
  const [historyProject, setHistoryProject] = useState(defaultModalState);
  const [types, setTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['tech', 'goal'])

  const addToast = useToast();

  useEffect(async () => {
    try {
      const result = await api.get(`/mng/strategies${selectedTypes.length ? `?type=${encodeURIComponent(JSON.stringify(selectedTypes))}` : ''}`);
      setIsLoaded(true);
      setProjects(
        result
          .sort((a, b) => sortByAlphaNum(a.num, b.num))
          .map(p => ({
            ...p,
            head: p.users.filter(u => u.role === 'head').map(u => u.displayName).join(', '),
            admin: p.users.filter(u => u.role === 'admin').map(u => u.displayName).join(', '),
            curator: p.users.filter(u => u.role === 'curator').map(u => u.displayName).join(', '),
            coordinator: p.users.filter(u => u.role === 'coordinator').map(u => u.displayName).join(', '),
          }))
      );
    } catch (error) {
      setIsLoaded(true);
      setError(error);
    }
  }, [selectedTypes]);

  useEffect(() => {
    const getTypes = async () => {
      const types = await api.get('/mng/strategies/types');
      setTypes(types.map(({ code, name }) => ({ value: code, label: name })));
    }
    getTypes()
  }, []);

  const hideStrategy = async (id, hide) => {
    const res = await api.post(`/mng/strategies/${id}/hide`, { strategyId: id, hide });
    if (res.id) {
      addToast.success('Изменения сохранены');
      setProjects((prevState) =>
        prevState.map((proj) =>
          proj.id === id ? { ...proj, isHidden: hide } : proj
        )
      );
    } else {
      addToast.error(res.error && res.error.message || 'Не удалось сохранить');
    }
  };

  const handleDelete = async () => {
    const result = await api.del(`/mng/strategies/${deleteProject.id}`);
    if (!result.error) {
      addToast.success('Стратегический проект удален');
      setProjects(prevState => prevState.filter(proj => proj.id !== deleteProject.id));
      setDeleteProject({show: false, id: undefined});
    }
  }

  if (error) {
    return (
      <div style={{ color: 'red', background: 'white' }}>
        Ошибка:
        {error.message}
      </div>
    );
  } if (!isLoaded) {
    return (
      <section className="page projects-page">
        <div className="container">
          <div>Загрузка...</div>
        </div>
      </section>
    );
  }
  return (
    <section className="page admin-strats-page">
      <div className="container">
        <BackBtn />
        <div style={styledTable}>
          <h3 style={styledTableHeading}>Реестр объектов Программы</h3>
          <MaterialTable
            options={modifyDefaultOptions({
              pageSize: 10,
              pageSizeOptions: [10, 30, 50],
              headerStyle: {
                fontSize: 12,
                lineHeight: 1.3,
                padding: '8px 16px',
                position: 'sticky',
                top: 0
              },
              filterCellStyle: {
                padding: '8px 16px',
                position: 'sticky',
                top: "36px",
                backgroundColor: 'white',
              },
              filtering: true,
              sorting: true,
              search: false,
              paging: false,
              maxBodyHeight: 'calc(100vh - 370px)'
            })}
            icons={tableIcons}
            localization={localization}
            components={{
              Toolbar: (props) => (
                <div style={styledToolbarContainer}>
                  <div>
                    <label htmlFor="types-select" style={{ marginBottom: '10px', fontWeight: '600' }}>
                      Тип объекта
                    </label>
                    <Select
                      value={types.filter(opt => selectedTypes.includes(opt.value))}
                      onChange={(opts) => setSelectedTypes(opts.map(opt => opt.value))}
                      options={types}
                      menuPortalTarget={document.body}
                      styles={{
                          menuPortal: (base) => ({...base, zIndex: 10000})
                      }}
                      isMulti
                      closeMenuOnSelect={false}
                      noOptionsMessage={() => 'Объектов не найдено'}
                      placeholder="Выберите тип объекта"
                    />
                  </div>
                  {/* <DownloadLink
                    variant="outline-primary"
                    url="/reports/projs/0/results/export?year=2021"
                    filename={`Выгрузка уник. результатов_${dateRenderer(new Date())}.xlsx`}
                  >
                    Выгрузить уникальные результаты
                  </DownloadLink> */}
                  <NavLink to="/strat-proj-create">
                    <Button variant="primary">Создать</Button>
                  </NavLink>
                </div>
              ),
            }}
            columns={[
              {
                title: <div style={{ width: '30px' }}>№</div>,
                field: 'num',
                filterCellStyle: { minWidth: '30px' },
                customSort: (a, b) => sortByAlphaNum(a.num, b.num),
              },
              { 
                title: 'Наименование проекта',
                field: 'name',
                filterCellStyle: { minWidth: '300px' },
                render: (rowData) => <span>{rowData.name} {rowData.isArchived && "(архив)"}</span>
              },
              {
                title: 'Руководитель',
                field: 'head',
                filterCellStyle: { minWidth: '350px' },
                render: (rowData) => rowData.head,
              },
              {
                title: 'Администратор',
                field: 'admin',
                filterCellStyle: { minWidth: '200px' },
                render: (rowData) => rowData.admin,
              },
              {
                title: 'Куратор',
                field: 'curator',
                filterCellStyle: { minWidth: '200px' },
                render: (rowData) => rowData.curator,
              },
              {
                title: 'Координатор',
                field: 'coordinator',
                filterCellStyle: { minWidth: '200px' },
                render: (rowData) => rowData.coordinator,
              },
              {
                title: 'Операции',
                field: '',
                filterCellStyle: { minWidth: '150px' },
                render: (rowData) => (
                  <div style={{ display: 'flex' }}>
                    {rowData.isHidden && (
                    <Button
                      className="me-1"
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => hideStrategy(rowData.id, false)}
                    >
                      <LockOpen />
                    </Button>
                    )}
                    {!rowData.isHidden && (
                    <Button
                      className="me-1"
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => hideStrategy(rowData.id, true)}
                    >
                      <Lock />
                    </Button>
                    )}
                    <NavLink to={`/strat-proj-create?id=${rowData.id}`}>
                      <Button className="me-1" size="sm" variant="outline-secondary"><Edit /></Button>
                    </NavLink>
                    <Button className="me-1" size="sm" variant="outline-danger" onClick={() => setDeleteProject({show: true, id: rowData.id})}>
                      <Delete/>
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={() => setHistoryProject({show: true, id: rowData.id, num: rowData.num})}>
                      <History/>
                    </Button>
                  </div>
                ),
              },
              {
                title: 'Тип объекта',
                field: 'type',
                filterCellStyle: { minWidth: '200px' },
                filtering: false,
                render: (rowData) => {
                  const type = types.find(t => t.value === rowData.type);
                  return type ? type.label : rowData.type;
                }
              },
              {
                title: 'Архив',
                field: 'type',
                filterCellStyle: { minWidth: '116px' },
                filtering: false,
                render: (rowData) => {
                  if (rowData.isArchived != null) {
                    return rowData.endDate
                      ? `с ${dateRenderer(new Date(rowData.endDate))}`
                      : 'Дата не указана';
                  }
                  return null;
                }
              },
            ]}
            data={projects}
          />
        </div>
      </div>
      <ConfirmModal
        show={deleteProject.show}
        onClose={() => setDeleteProject(defaultModalState)}
        onConfirm={handleDelete}
        message='Вы действительно хотите удалить стратегический проект/политику?'
        headerText='Подтвердите действие'
      />
      <SPHistory
        show={historyProject.show}
        onHide={() => setHistoryProject(defaultModalState)}
        id={historyProject.id}
        num={historyProject.num}
      />
    </section>
  );
}

const styledToolbarContainer = { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', margin: '20px 0', gap: '1rem' };
